import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAppStore } from '../store';
import type { Space } from '../types';

export function useSpaces() {
  const setSpaces = useAppStore((s) => s.setSpaces);
  const { data } = useQuery({
    queryKey: ['spaces'],
    queryFn: async () => {
      const spaces = await api.spaces.list();
      setSpaces(spaces);
      return spaces;
    },
  });
  return (data ?? []) as Space[];
}

export function useCurrentSpace(): Space | undefined {
  const spaces = useAppStore((s) => s.spaces);
  const currentSpaceId = useAppStore((s) => s.currentSpaceId);
  return spaces.find((s) => s.id === currentSpaceId);
}

export function useSpaceById(id: string | undefined | null): Space | undefined {
  const spaces = useAppStore((s) => s.spaces);
  if (!id) return undefined;
  return spaces.find((s) => s.id === id);
}

export function useCreateSpace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; color: string; private?: boolean }) =>
      api.spaces.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

export function useUpdateSpace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<{ name: string; color: string; private: boolean }> }) =>
      api.spaces.update(id, changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });
}

export function useDeleteSpace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetSpaceId }: { id: string; targetSpaceId: string }) =>
      api.spaces.delete(id, targetSpaceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
